import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  useCreateOrgMutation,
  useUpdateOrgMutation,
  useGetOrgQuery,
  useUploadOrgLogoMutation,
  useDeleteOrgLogoMutation,
  useLookupPostalCodeQuery,
} from '@proaxiv/server-api'
import type {
  GetOrganizationResponse,
  PostalCodeLookupResponse,
  PostalCodeCandidate,
} from '@proaxiv/server-api'
import { validateImageUpload, compressImageFile } from '@proaxiv/ui'
import { Button, ButtonLink } from '../components/Button'
import { ErrorModal } from '../components/ErrorModal'
import { useActiveOrg } from '../contexts/ActiveOrgContext'
import { COUNTRIES, DEFAULT_COUNTRY_CODE, getCountryName } from '../utils/countries'

interface CompanyData {
  name: string
  description: string
  country: string
  region: string
  postalCode: string
  city: string
  street: string
  building: string
  addressAdditional: string
}

type FieldErrors = Partial<Record<keyof CompanyData, string>>

// ── Field ──────────────────────────────────────────────────────────────────

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  id: string
  prefix?: string
  suffix?: React.ReactNode
  required?: boolean
  error?: string
  /**
   * Marks the field as recently auto-filled (e.g. by the postal code lookup): shows a
   * persistent badge (text supplied by the caller via `autoFilledLabel`, already
   * translated) next to the label plus a subtle border tint. The caller clears this once
   * the user edits this specific field — never relies on a timeout, so users who look
   * away don't lose the explanation of what happened.
   */
  autoFilled?: boolean
  /** Already-translated label for the auto-filled badge, e.g. `t('new_org_field_autofilled')`. */
  autoFilledLabel?: string
}

function Field({
  label,
  id,
  prefix,
  suffix,
  required,
  error,
  autoFilled,
  autoFilledLabel,
  ...rest
}: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-pax-black flex flex-wrap items-center gap-x-2 text-sm">
        <span>
          {label}
          {required && <span className="text-pax-red ml-0.5">*</span>}
        </span>
        {autoFilled && (
          <span className="bg-pax-green/15 text-pax-green rounded-full px-2 py-0.5 text-[10px] font-semibold">
            {autoFilledLabel}
          </span>
        )}
      </label>
      <div
        className={[
          'flex items-center rounded-xl border bg-white px-3 py-2 transition-colors focus-within:ring-1',
          error
            ? 'border-pax-red focus-within:border-pax-red focus-within:ring-pax-red/30'
            : autoFilled
              ? 'border-pax-green/40 focus-within:border-pax-blue focus-within:ring-pax-blue/30'
              : 'border-pax-purple/30 focus-within:border-pax-blue focus-within:ring-pax-blue/30',
        ].join(' ')}
      >
        {prefix && (
          <span className="text-pax-gray mr-2 shrink-0 text-sm font-semibold">{prefix}</span>
        )}
        <input
          id={id}
          {...rest}
          className="text-pax-black placeholder:text-pax-gray/50 w-full border-0 bg-transparent text-sm outline-none"
        />
        {suffix && <span className="ml-2 flex shrink-0 items-center">{suffix}</span>}
      </div>
      {error && <p className="text-pax-red text-xs">{error}</p>}
    </div>
  )
}

interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string
  id: string
  required?: boolean
  error?: string
  placeholder?: string
}

function SelectField({
  label,
  id,
  required,
  error,
  placeholder,
  children,
  ...rest
}: SelectFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-pax-black text-sm">
        {label}
        {required && <span className="text-pax-red ml-0.5">*</span>}
      </label>
      <div
        className={[
          'flex items-center rounded-xl border bg-white px-3 py-2 transition-colors focus-within:ring-1',
          error
            ? 'border-pax-red focus-within:border-pax-red focus-within:ring-pax-red/30'
            : 'border-pax-purple/30 focus-within:border-pax-blue focus-within:ring-pax-blue/30',
        ].join(' ')}
      >
        <select
          id={id}
          {...rest}
          className="text-pax-black placeholder:text-pax-gray/50 w-full border-0 bg-transparent text-sm outline-none"
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children}
        </select>
      </div>
      {error && <p className="text-pax-red text-xs">{error}</p>}
    </div>
  )
}

interface TextareaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  id: string
}

function TextareaField({ label, id, ...rest }: TextareaFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-pax-black text-sm">
        {label}
      </label>
      <textarea
        id={id}
        {...rest}
        className="text-pax-black focus:border-pax-blue border-pax-purple/30 placeholder:text-pax-gray/50 min-h-[100px] resize-y rounded-xl border bg-white px-3 py-2 text-sm transition-colors outline-none"
      />
    </div>
  )
}

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div>
      <h2 className="text-pax-black text-xs font-bold tracking-widest uppercase">{title}</h2>
      {subtitle && <p className="text-pax-gray mt-1 text-xs">{subtitle}</p>}
    </div>
  )
}

// ── LogoUploadField ────────────────────────────────────────────────────────

const SpinnerIcon = ({ className = 'h-3.5 w-3.5' }: { className?: string }) => (
  <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
    />
  </svg>
)

const CheckCircleIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
    <path
      d="M8 12.5l2.5 2.5 5.5-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

function LogoUploadField({
  preview,
  onChange,
  onClear,
}: {
  preview: string | null
  onChange: (file: File, previewUrl: string) => void
  onClear: () => void
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  // Client-side compression (see `compressImageFile`) can take a few noticeable seconds for a
  // large source image — without this, the thumbnail/buttons just sit inert with no indication
  // anything is happening, which reads as a broken upload rather than "still working".
  const [isProcessing, setIsProcessing] = useState(false)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const error = validateImageUpload(file, {
      notImage: t('logo_type_error'),
      tooLarge: t('logo_size_error'),
    })
    if (error) {
      alert(error)
      return
    }
    setIsProcessing(true)
    try {
      const compressed = await compressImageFile(file)
      const url = URL.createObjectURL(compressed)
      // Clear processing before notifying the parent so the preview and the Remove
      // control appear in the same paint — otherwise waitFor(preview) can pass while
      // Remove is still gated on !isProcessing (flake under suite load).
      setIsProcessing(false)
      onChange(compressed, url)
    } catch {
      setIsProcessing(false)
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-pax-black text-sm">{t('logo_label')}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={isProcessing}
          className="border-pax-purple/30 hover:border-pax-blue relative flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-white transition-colors disabled:cursor-not-allowed"
          aria-label={t('logo_upload')}
        >
          {preview ? (
            <img src={preview} alt="Logo preview" className="h-full w-full object-contain p-1" />
          ) : (
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-pax-gray/50"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
          )}
          {isProcessing && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-white/80"
              aria-hidden="true"
            >
              <SpinnerIcon className="text-pax-blue h-5 w-5" />
            </div>
          )}
        </button>
        <div className="flex flex-col gap-1">
          {isProcessing ? (
            <span className="text-pax-blue flex items-center gap-1.5 text-xs font-medium">
              <SpinnerIcon />
              {t('logo_processing')}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-pax-blue text-xs font-medium hover:underline focus-visible:outline-none"
            >
              {preview ? t('logo_replace') : t('logo_upload')}
            </button>
          )}
          {preview && (
            <button
              type="button"
              onClick={onClear}
              disabled={isProcessing}
              className="text-pax-gray text-xs hover:underline focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('logo_remove')}
            </button>
          )}
          <span className="text-pax-gray text-xs">{t('logo_constraints')}</span>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFile}
        disabled={isProcessing}
      />
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

/** Single-step form for creating or editing an organization. */
export function NewOrganizationPage() {
  const { t, i18n } = useTranslation()
  const { setActiveOrgId } = useActiveOrg()

  const [createOrg, { isLoading: isCreating }] = useCreateOrgMutation()
  const [updateOrg, { isLoading: isUpdating }] = useUpdateOrgMutation()
  const [uploadOrgLogo, { isLoading: isUploadingLogo }] = useUploadOrgLogoMutation()
  const [deleteOrgLogo, { isLoading: isDeletingLogo }] = useDeleteOrgLogoMutation()
  const isSaving = isCreating || isUpdating || isUploadingLogo || isDeletingLogo

  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()

  const {
    data: rawOrg,
    isLoading: isOrgLoading,
    isError: isOrgError,
  } = useGetOrgQuery({ orgId: id! }, { skip: !isEdit || !id })
  const org = rawOrg as GetOrganizationResponse | undefined

  const [saveError, setSaveError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const formSeeded = useRef(false)

  const [company, setCompany] = useState<CompanyData>({
    name: '',
    description: '',
    // Pre-select Japan for brand-new organizations, since most of the user base is Japanese
    // (issue #202) — left blank in edit mode, where the effect below seeds it from the org.
    country: isEdit ? '' : DEFAULT_COUNTRY_CODE,
    region: '',
    postalCode: '',
    city: '',
    street: '',
    building: '',
    addressAdditional: '',
  })

  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  // True once the user clears an existing (server-persisted) logo — signals that Save must
  // call the delete endpoint, since clearing local state alone never removes it server-side.
  const [logoRemoved, setLogoRemoved] = useState(false)

  // Postal code lookup: debounced so it fires automatically as the user types (no Search
  // button), since we don't want to hammer the third-party providers on every keystroke.
  // The dropdown is left open for the user to confirm which candidate to apply, even when
  // there's only one, since silently overwriting region/city/street felt surprising in review.
  const [postalDebounced, setPostalDebounced] = useState<{ country: string; code: string } | null>(
    null
  )
  const [postalDropdownOpen, setPostalDropdownOpen] = useState(false)
  const [postalSearchNotice, setPostalSearchNotice] = useState<string | null>(null)
  // True once the user has picked a candidate for the postal code currently entered — drives
  // the persistent "we filled these in" confirmation (issue: organizers were not noticing the
  // silent autofill) and stops re-showing the picker on refocus so it can't cover the very
  // fields it just filled.
  const [postalApplied, setPostalApplied] = useState(false)
  // Which of region/city/street were actually set by the last applied candidate — drives the
  // per-field "Auto-filled" badge. A field's flag clears the moment the user edits it, so the
  // badge never lingers on a value the organizer has since changed themselves.
  const [autoFilledFields, setAutoFilledFields] = useState<
    Partial<Record<'region' | 'city' | 'street', boolean>>
  >({})
  const postalFieldWrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const country = company.country
    const code = company.postalCode.trim()
    // A new postal code invalidates any previous "we filled these in" confirmation, badges and
    // candidate list — clear them immediately rather than leaving stale results up (or a stale
    // dropdown open) while the next lookup runs.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPostalApplied(false)
    setAutoFilledFields({})
    setPostalDropdownOpen(false)
    setPostalSearchNotice(null)
    if (!country || !code) {
      setPostalDebounced(null)
      return
    }
    const handle = setTimeout(() => {
      setPostalDebounced({ country, code })
    }, 500)
    return () => clearTimeout(handle)
  }, [company.country, company.postalCode])

  const {
    data: postalDataRaw,
    isFetching: isSearchingPostal,
    isError: isPostalNetworkError,
  } = useLookupPostalCodeQuery(
    { country: postalDebounced?.country ?? '', code: postalDebounced?.code ?? '' },
    { skip: !postalDebounced }
  )
  const postalData = postalDataRaw as PostalCodeLookupResponse | undefined
  const postalCandidates = postalData?.candidates ?? []

  useEffect(() => {
    if (!postalDebounced) return
    if (isPostalNetworkError || !postalData || !postalData.success) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPostalDropdownOpen(false)
      setPostalSearchNotice(t('new_org_postal_search_failed'))
      return
    }
    if ((postalData.candidates ?? []).length === 0) {
      setPostalDropdownOpen(false)
      setPostalSearchNotice(t('new_org_postal_search_no_results'))
    } else {
      setPostalDropdownOpen(true)
      setPostalSearchNotice(null)
    }
    // Only re-run when a new lookup resolves — reacting to `t`/`postalDebounced` here would
    // re-open the dropdown on every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalData, isPostalNetworkError])

  // Close the dropdown on outside clicks, mirroring the org switcher's click-away pattern.
  useEffect(() => {
    if (!postalDropdownOpen) return
    function handleClickAway(e: MouseEvent) {
      if (!postalFieldWrapRef.current?.contains(e.target as Node)) {
        setPostalDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickAway)
    return () => document.removeEventListener('mousedown', handleClickAway)
  }, [postalDropdownOpen])

  function applyPostalCandidate(candidate: PostalCodeCandidate) {
    setCompany((p) => ({
      ...p,
      region: candidate.region || p.region,
      city: candidate.city || p.city,
      street: candidate.street || p.street,
    }))
    setFieldErrors((prev) => ({ ...prev, region: undefined, city: undefined, street: undefined }))
    setAutoFilledFields({
      region: !!candidate.region,
      city: !!candidate.city,
      street: !!candidate.street,
    })
    setPostalApplied(true)
    setPostalDropdownOpen(false)
    setPostalSearchNotice(null)
  }

  useEffect(() => {
    if (isEdit && org?.success && !formSeeded.current) {
      formSeeded.current = true
      setCompany({
        name: org.name ?? '',
        description: org.description ?? '',
        country: org.address?.country ?? '',
        region: org.address?.region ?? '',
        postalCode: org.address?.postalCode ?? '',
        city: org.address?.city ?? '',
        street: org.address?.street ?? '',
        building: org.address?.building ?? '',
        addressAdditional: org.address?.additional ?? '',
      })
      setLogoPreview(org.logoUrl ?? null)
    }
  }, [isEdit, org])

  // Guard: edit route — loading state
  if (isEdit && isOrgLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-8">
        <p className="text-pax-gray text-sm">{t('loading')}</p>
      </div>
    )
  }

  // Guard: edit route with an unknown or errored id
  if (isEdit && (isOrgError || (org && !org.success))) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-8">
        <Link
          to="/organizations"
          className="text-pax-black hover:text-pax-blue mb-8 flex items-center gap-2 text-sm font-medium transition-colors focus-visible:outline-none"
        >
          ← {t('nav_back')}
        </Link>
        <div className="border-pax-purple/20 flex flex-col items-center gap-4 rounded-2xl border bg-white p-12 text-center">
          <p className="text-pax-gray text-5xl font-black opacity-20">404</p>
          <p className="text-pax-black text-lg font-semibold">{t('org_not_found_title')}</p>
          <p className="text-pax-gray text-sm">{t('org_not_found_desc')}</p>
        </div>
      </div>
    )
  }

  function validate(): FieldErrors {
    const errors: FieldErrors = {}
    const required = t('validation_required')
    if (!company.name.trim()) errors.name = required
    if (!company.country) errors.country = required
    if (!company.region.trim()) errors.region = required
    if (!company.postalCode.trim()) errors.postalCode = required
    if (!company.city.trim()) errors.city = required
    if (!company.street.trim()) errors.street = required
    return errors
  }

  async function handleSave() {
    const errors = validate()
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})
    setSaveError(null)
    try {
      let resolvedOrgId: string | undefined
      const address = {
        country: company.country,
        region: company.region.trim(),
        postalCode: company.postalCode.trim(),
        city: company.city.trim(),
        street: company.street.trim(),
        building: company.building.trim() || undefined,
        additional: company.addressAdditional.trim() || undefined,
      }
      if (isEdit && id) {
        const result = await updateOrg({
          orgId: id,
          updateOrgBody: {
            name: company.name.trim() || org?.name || undefined,
            description: company.description.trim() || org?.description || undefined,
            address,
          },
        })
        if ('error' in result) throw result.error
        resolvedOrgId = id
      } else {
        const result = await createOrg({
          createOrgBody: {
            name: company.name.trim(),
            description: company.description.trim() || undefined,
            address,
          },
        })
        if ('error' in result) throw result.error
        resolvedOrgId = (result.data as { orgId?: string } | undefined)?.orgId
      }
      if (logoFile && resolvedOrgId) {
        const formData = new FormData()
        formData.append('file', logoFile)
        await uploadOrgLogo({
          orgId: resolvedOrgId,
          body: formData as unknown as { file: Blob },
        })
        setLogoRemoved(false)
      } else if (logoRemoved && isEdit && resolvedOrgId) {
        // Only an existing org can have a server-persisted logo to remove — a brand-new org
        // never had one, so there is nothing to delete even if the user toggled Remove pre-save.
        await deleteOrgLogo({ orgId: resolvedOrgId })
        setLogoRemoved(false)
      }
      if (!isEdit && resolvedOrgId) {
        setActiveOrgId(resolvedOrgId)
      }
      // Both flows land on the same place — the org's own page — so there's no separate
      // intermediate confirmation screen to show on success, just a straight redirect back.
      const destination = isEdit && id ? id : resolvedOrgId
      navigate(destination ? `/organizations/${destination}` : '/organizations')
    } catch (err) {
      void err
      setSaveError(t('error_save_org'))
    }
  }

  function clearAutoFilledFlag(key: 'region' | 'city' | 'street') {
    setAutoFilledFields((prev) => (prev[key] ? { ...prev, [key]: false } : prev))
  }

  function handleFieldChange<K extends keyof CompanyData>(key: K, value: CompanyData[K]) {
    setCompany((p) => ({ ...p, [key]: value }))
    if (fieldErrors[key]) setFieldErrors((prev) => ({ ...prev, [key]: undefined }))
    // The organizer just corrected this field by hand — the "Auto-filled" badge no longer
    // describes its current value, so drop it instead of leaving a stale claim.
    if (key === 'region' || key === 'city' || key === 'street') clearAutoFilledFlag(key)
  }

  const backPath = isEdit ? `/organizations/${id}` : '/organizations'

  return (
    <div className="flex min-h-[calc(100vh-64px)] flex-col items-center px-4 py-6 md:py-8">
      {/* Breadcrumb */}
      <div className="w-full max-w-[500px]">
        <Link
          to={backPath}
          className="text-pax-gray hover:text-pax-blue mb-4 flex items-center gap-1.5 text-sm transition-colors focus-visible:outline-none"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          {t('nav_back')}
        </Link>
      </div>

      {/* Heading */}
      <div className="mb-6 text-center">
        <h1 className="font-display text-pax-black text-2xl font-bold tracking-tight md:text-4xl">
          {isEdit ? t('edit_org_title') : t('new_org_title')}
        </h1>
        <p className="text-pax-gray mt-1 text-sm">
          {isEdit ? t('edit_org_subtitle') : t('new_org_subtitle')}
        </p>
      </div>

      {/* Card */}
      <div className="border-pax-purple/25 w-full max-w-[500px] rounded-2xl border bg-white p-6 shadow-sm">
        {/* Company Details form */}
        <div className="flex flex-col gap-4">
          <SectionHeading
            title={t('new_org_company_heading')}
            subtitle={t('new_org_company_desc')}
          />
          <hr className="border-pax-purple/20" />
          <LogoUploadField
            preview={logoPreview}
            onChange={(file, url) => {
              setLogoFile(file)
              setLogoPreview(url)
              setLogoRemoved(false)
            }}
            onClear={() => {
              setLogoFile(null)
              setLogoPreview(null)
              setLogoRemoved(true)
            }}
          />
          <Field
            id="company-name"
            label={t('new_org_field_company_name')}
            required
            placeholder={t('new_org_field_company_name_placeholder')}
            value={company.name}
            error={fieldErrors.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
          />
          <TextareaField
            id="company-description"
            label={t('new_org_field_company_desc')}
            placeholder={t('new_org_field_company_desc_placeholder')}
            value={company.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
          />
          <p className="text-pax-black pt-1 text-xs font-bold tracking-widest uppercase">
            {t('new_org_address_heading')}
          </p>
          <SelectField
            id="country"
            label={t('new_org_field_country')}
            required
            placeholder={t('new_org_field_country_placeholder')}
            value={company.country}
            error={fieldErrors.country}
            onChange={(e) => handleFieldChange('country', e.target.value)}
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {getCountryName(c.code, i18n.language)}
              </option>
            ))}
          </SelectField>
          <div ref={postalFieldWrapRef} className="relative">
            <Field
              id="postal-code"
              label={t('new_org_field_postal')}
              required
              placeholder={t('new_org_field_postal_placeholder')}
              value={company.postalCode}
              error={fieldErrors.postalCode}
              onChange={(e) => handleFieldChange('postalCode', e.target.value)}
              onFocus={() => {
                // Only reopen the picker on refocus before a candidate has been chosen for this
                // code — once applied, reopening it here (an implicit, easy-to-miss trigger)
                // used to cover the very fields it had just filled. Reopening afterwards is
                // done explicitly via the "Change address" link below instead.
                if (!postalApplied && postalCandidates.length > 0) setPostalDropdownOpen(true)
              }}
              autoComplete="off"
              suffix={
                isSearchingPostal ? (
                  <SpinnerIcon className="text-pax-gray h-3.5 w-3.5" />
                ) : undefined
              }
            />
            {postalDropdownOpen && postalCandidates.length > 0 && (
              <div className="border-pax-purple/30 absolute top-full right-0 left-0 z-10 mt-1 flex max-h-64 flex-col gap-0.5 overflow-y-auto rounded-xl border bg-white p-1.5 shadow-lg">
                <p className="text-pax-gray px-2 pt-1 pb-1.5 text-xs">
                  {t('new_org_postal_candidates_heading')}
                </p>
                {postalCandidates.map((candidate, i) => (
                  <button
                    key={`${candidate.label}-${i}`}
                    type="button"
                    onClick={() => applyPostalCandidate(candidate)}
                    className="text-pax-black hover:bg-pax-purple/10 cursor-pointer rounded-lg px-2 py-1.5 text-left text-sm transition-colors focus-visible:outline-none"
                  >
                    {candidate.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {isSearchingPostal ? (
            // While the debounced lookup is in flight, say so explicitly — the small spinner
            // inside the field alone was easy to miss, and organizers could not tell whether
            // the dropdown that follows is still coming or simply has nothing to show.
            <p className="text-pax-gray -mt-2 flex items-center gap-1.5 text-xs">
              <SpinnerIcon className="h-3 w-3 shrink-0" />
              {t('new_org_postal_searching')}
            </p>
          ) : postalApplied ? (
            <div className="bg-pax-green/8 border-pax-green/25 -mt-2 flex items-start gap-2 rounded-lg border px-3 py-2.5">
              <CheckCircleIcon className="text-pax-green mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex flex-1 flex-col items-start gap-1">
                <p className="text-pax-black text-xs">{t('new_org_postal_applied_notice')}</p>
                {postalCandidates.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setPostalApplied(false)
                      setPostalDropdownOpen(true)
                    }}
                    className="text-pax-blue cursor-pointer text-xs font-medium underline-offset-2 hover:underline focus-visible:outline-none"
                  >
                    {t('new_org_postal_change_address')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            postalSearchNotice && (
              <p className="text-pax-gray -mt-2 text-xs">{postalSearchNotice}</p>
            )
          )}
          <Field
            id="region"
            label={t('new_org_field_region')}
            required
            placeholder={t('new_org_field_region_placeholder')}
            value={company.region}
            error={fieldErrors.region}
            autoFilled={autoFilledFields.region}
            autoFilledLabel={t('new_org_field_autofilled')}
            onChange={(e) => handleFieldChange('region', e.target.value)}
          />
          <Field
            id="city"
            label={t('new_org_field_city')}
            required
            placeholder={t('new_org_field_city_placeholder')}
            value={company.city}
            error={fieldErrors.city}
            autoFilled={autoFilledFields.city}
            autoFilledLabel={t('new_org_field_autofilled')}
            onChange={(e) => handleFieldChange('city', e.target.value)}
          />
          <Field
            id="street"
            label={t('new_org_field_street')}
            required
            placeholder={t('new_org_field_street_placeholder')}
            value={company.street}
            error={fieldErrors.street}
            autoFilled={autoFilledFields.street}
            autoFilledLabel={t('new_org_field_autofilled')}
            onChange={(e) => handleFieldChange('street', e.target.value)}
          />
          <Field
            id="building"
            label={t('new_org_field_building')}
            placeholder={t('new_org_field_building_placeholder')}
            value={company.building}
            onChange={(e) => handleFieldChange('building', e.target.value)}
          />
          <Field
            id="address-additional"
            label={t('new_org_field_additional')}
            placeholder={t('new_org_field_additional_placeholder')}
            value={company.addressAdditional}
            onChange={(e) => handleFieldChange('addressAdditional', e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-1">
            <ButtonLink variant="secondary" to={backPath}>
              {t('btn_cancel_form')}
            </ButtonLink>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
              leftIcon={isSaving ? <SpinnerIcon /> : undefined}
            >
              {isSaving ? t('btn_saving') : t('btn_save')}
            </Button>
          </div>
        </div>
      </div>

      {saveError && (
        <ErrorModal
          title={t('error_save_org_title')}
          message={saveError}
          onClose={() => setSaveError(null)}
        />
      )}
    </div>
  )
}
